import { useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useEvent } from '../store';
import { useAuth } from '../store';
import { saveEvent, isUltraAdmin } from '../utils';
import { saveTemplate } from '../services/firestore';
import { parseOddsText, createFightFromParsedData } from '../utils/oddsParser';
import type { Fight, Fighter, Event } from '../types';

/**
 * Add Fight Page
 * Admin page for adding a new fight to the event
 * ONLY WORKS FOR TEMPLATES - Ultra-admin only
 */
export function AddFightPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event, addFight } = useEvent();
  const { currentUser } = useAuth();
  
  const [entryMode, setEntryMode] = useState<'manual' | 'paste'>('manual');
  const [pastedOddsText, setPastedOddsText] = useState('');
  const [fighter1Name, setFighter1Name] = useState('');
  const [fighter1Odds, setFighter1Odds] = useState('');
  const [fighter2Name, setFighter2Name] = useState('');
  const [fighter2Odds, setFighter2Odds] = useState('');
  const [error, setError] = useState('');
  const [parsedPreview, setParsedPreview] = useState<string | null>(null);

  // Block access for non-templates or non-ultra-admins
  if (event && !event.isTemplate) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-primary-text mb-4">Access Denied</h2>
          <p className="text-secondary-text mb-4">
            Fights cannot be added to event instances. They are managed through the template.
          </p>
          <Button onClick={() => navigate(`/event/${eventId}`)}>
            Back to Event
          </Button>
        </Card>
      </div>
    );
  }

  if (currentUser && !isUltraAdmin(currentUser)) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <h2 className="text-xl font-bold text-primary-text mb-4">Ultra Admin Only</h2>
          <p className="text-secondary-text mb-4">
            Only ultra-admins can manage template fights.
          </p>
          <Button onClick={() => navigate('/games')}>
            Back to Games
          </Button>
        </Card>
      </div>
    );
  }

  // Helper to save event or template depending on type
  const saveEventOrTemplate = async (eventData: Event) => {
    if (eventData.isTemplate) {
      await saveTemplate(eventData);
    } else {
      await saveEvent(eventData);
    }
  };

  const handleParseOdds = () => {
    setError('');
    setParsedPreview(null);

    if (!pastedOddsText.trim()) {
      setError('Please paste odds data');
      return;
    }

    const parsed = parseOddsText(pastedOddsText);
    if (!parsed) {
      setError('Could not parse odds data. Please check the format and try again.');
      return;
    }

    // Create preview
    const preview = `Fighters: ${parsed.fighter1} vs ${parsed.fighter2}
Moneyline Odds: ${parsed.fighter1Odds} / ${parsed.fighter2Odds}
Markets Found: ${parsed.markets.length}
${parsed.markets.map(m => `  - ${m.name}: ${m.outcomes.length} outcomes`).join('\n')}`;
    
    setParsedPreview(preview);
    setError('');
  };

  const handleSubmitFromPaste = async () => {
    if (!pastedOddsText.trim()) {
      setError('Please paste odds data');
      return;
    }

    const parsed = parseOddsText(pastedOddsText);
    if (!parsed) {
      setError('Could not parse odds data. Please check the format and try again.');
      return;
    }

    if (!event) {
      setError('Event not found');
      return;
    }

    try {
      // Create fight from parsed data
      const fight = createFightFromParsedData(event.id, parsed);

      // Add fight to event
      addFight(fight);

      // Save event or template
      const updatedEvent = {
        ...event,
        fights: [...event.fights, fight],
      };
      await saveEventOrTemplate(updatedEvent);

      // Navigate back to admin panel
      navigate(`/admin/${eventId}`);
    } catch (err) {
      setError('Failed to add fight. Please try again.');
      console.error('Error adding fight:', err);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!fighter1Name.trim()) {
      setError('Fighter 1 name is required');
      return;
    }

    if (!fighter2Name.trim()) {
      setError('Fighter 2 name is required');
      return;
    }

    const odds1 = parseFloat(fighter1Odds);
    const odds2 = parseFloat(fighter2Odds);

    if (isNaN(odds1)) {
      setError('Fighter 1 odds must be a valid number');
      return;
    }

    if (isNaN(odds2)) {
      setError('Fighter 2 odds must be a valid number');
      return;
    }

    if (!event) {
      setError('Event not found');
      return;
    }

    try {
      // Create fighters
      const fighter1: Fighter = {
        id: `fighter-${Date.now()}-1`,
        name: fighter1Name.trim(),
        odds: odds1,
      };

      const fighter2: Fighter = {
        id: `fighter-${Date.now()}-2`,
        name: fighter2Name.trim(),
        odds: odds2,
      };

      // Create moneyline market from fighters
      const moneylineMarket = {
        name: 'Moneyline',
        key: 'moneyline',
        outcomes: [
          { id: `moneyline-${fighter1.name}`, name: fighter1.name, odds: fighter1.odds },
          { id: `moneyline-${fighter2.name}`, name: fighter2.name, odds: fighter2.odds },
        ],
      };

      // Create fight with moneyline market
      const fight: Fight = {
        id: `fight-${Date.now()}`,
        eventId: event.id,
        fighters: [fighter1, fighter2],
        markets: [moneylineMarket],
        status: 'upcoming',
      };

      // Add fight to event
      addFight(fight);

      // Save event or template
      const updatedEvent = {
        ...event,
        fights: [...event.fights, fight],
      };
      await saveEventOrTemplate(updatedEvent);

      // Navigate back to admin panel
      navigate(`/admin/${eventId}`);
    } catch (err) {
      setError('Failed to add fight. Please try again.');
      console.error('Error adding fight:', err);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary-text">Add Fight</h1>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/admin/${eventId}`)}
          >
            Cancel
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <Card padding="lg">
          {/* Entry Mode Toggle */}
          <div className="mb-6">
            <div className="flex gap-2 border-b border-border">
              <button
                type="button"
                onClick={() => setEntryMode('manual')}
                className={`px-4 py-2 font-medium transition-colors ${
                  entryMode === 'manual'
                    ? 'text-primary-text border-b-2 border-primary-blue'
                    : 'text-secondary-text hover:text-primary-text'
                }`}
              >
                Manual Entry
              </button>
              <button
                type="button"
                onClick={() => setEntryMode('paste')}
                className={`px-4 py-2 font-medium transition-colors ${
                  entryMode === 'paste'
                    ? 'text-primary-text border-b-2 border-primary-blue'
                    : 'text-secondary-text hover:text-primary-text'
                }`}
              >
                Paste Odds Data
              </button>
            </div>
          </div>

          {entryMode === 'paste' ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-primary-text mb-2">
                  Paste Odds Data
                </label>
                <textarea
                  className="w-full px-3 py-2 bg-tertiary-bg border border-border rounded-lg text-sm text-primary-text font-mono"
                  rows={15}
                  placeholder="Paste the odds data here (e.g., Gabriel Bonfim vs Randy Brown...)"
                  value={pastedOddsText}
                  onChange={(e) => {
                    setPastedOddsText(e.target.value);
                    setParsedPreview(null);
                    setError('');
                  }}
                />
                <p className="text-xs text-secondary-text mt-1">
                  Paste the complete odds data including fighter names, moneyline odds, totals, and prop bets.
                </p>
                <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-400">
                  <strong>⚠️ Tip:</strong> On Bovada, click ALL "Show More" links before copying to capture all betting markets. 
                  On mobile, scroll down and expand all sections first.
                </div>
              </div>

              <Button
                type="button"
                variant="secondary"
                onClick={handleParseOdds}
                fullWidth
              >
                Parse Odds Data
              </Button>

              {parsedPreview && (
                <div className="p-4 bg-success-green/10 border border-success-green rounded-lg">
                  <h3 className="text-sm font-semibold text-success-green mb-2">Parsed Successfully!</h3>
                  <pre className="text-xs text-primary-text whitespace-pre-wrap font-mono">
                    {parsedPreview}
                  </pre>
                </div>
              )}

              {error && (
                <div className="p-3 bg-error/10 border border-error rounded-lg">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate(`/admin/${eventId}`)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={handleSubmitFromPaste}
                  disabled={!parsedPreview}
                >
                  Create Fight with All Markets
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary-text mb-4">Fighter 1</h2>
              <div className="space-y-4">
                <Input
                  label="Fighter Name"
                  placeholder="e.g., Jon Jones"
                  value={fighter1Name}
                  onChange={(e) => setFighter1Name(e.target.value)}
                  error={error && error.includes('Fighter 1') ? error : ''}
                  autoFocus
                />
                <Input
                  label="Odds"
                  placeholder="e.g., -150 or +200"
                  type="number"
                  step="any"
                  value={fighter1Odds}
                  onChange={(e) => setFighter1Odds(e.target.value)}
                  error={error && error.includes('Fighter 1 odds') ? error : ''}
                  helperText="American odds format (e.g., -150, +200)"
                />
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h2 className="text-lg font-semibold text-primary-text mb-4">Fighter 2</h2>
              <div className="space-y-4">
                <Input
                  label="Fighter Name"
                  placeholder="e.g., Stipe Miocic"
                  value={fighter2Name}
                  onChange={(e) => setFighter2Name(e.target.value)}
                  error={error && error.includes('Fighter 2') ? error : ''}
                />
                <Input
                  label="Odds"
                  placeholder="e.g., -150 or +200"
                  type="number"
                  step="any"
                  value={fighter2Odds}
                  onChange={(e) => setFighter2Odds(e.target.value)}
                  error={error && error.includes('Fighter 2 odds') ? error : ''}
                  helperText="American odds format (e.g., -150, +200)"
                />
              </div>
            </div>

            {error && !error.includes('Fighter') && (
              <div className="p-3 bg-error/10 border border-error rounded-lg">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="secondary"
                fullWidth
                onClick={() => navigate(`/admin/${eventId}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                fullWidth
              >
                Add Fight
              </Button>
            </div>
          </form>
          )}
        </Card>
      </div>
    </div>
  );
}

